import React, { useCallback, useEffect, useState } from "react";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";
import Modal from "./Modal";
import LoadingSpinner from "./LoadingSpinner";
import { FortinetLastScan, FortinetTestResult, FortinetTraceSwitch } from "./FortinetDiagnostics";

/**
 * Super Admin page: Fortinet diagnostics for one organization (GET / POST
 * /SuperAdmin/organizations/{id}/fortinet[/trace]). A SuperAdmin has no implicit org role and so
 * cannot reach the Fortinet card in a customer org's Integration Settings; this panel shows the
 * same last-scan line and trace switch, plus the automatic-scanning switch (POST .../fortinet/auto-scan)
 * and Enrich risks (POST .../fortinet/enrich: queues the org's Fortinet risks with no AI analysis;
 * ?reanalyze=FirewallPolicy re-queues every firewall policy risk).
 * All three mirror CanManageFortinetTracing.
 */
const SuperAdminFortinetPanel = ({ org, onClose }) => {
  const apiCall = useApi();
  const { canManageFortinetTracing } = useRBAC();
  const orgId = org?.organizationId;

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [traceSaving, setTraceSaving] = useState(false);
  const [autoScanSaving, setAutoScanSaving] = useState(false);
  // TEMPORARY (revert this commit): Test connection for listed diagnostic operators only.
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [enriching, setEnriching] = useState(false);
  const [enrichMessage, setEnrichMessage] = useState(null);

  useEffect(() => {
    if (orgId == null) return undefined;
    let cancelled = false;
    setStatus(null); setError(null); setLoading(true); setEnrichMessage(null);
    apiCall("GET", `/SuperAdmin/organizations/${orgId}/fortinet`)
      .then((data) => { if (!cancelled) setStatus(data); })
      .catch((err) => { if (!cancelled) setError(err?.response?.data?.message || "Failed to load Fortinet status."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [apiCall, orgId]);

  const handleTrace = useCallback(async (enabled) => {
    setTraceSaving(true); setError(null);
    try {
      const updated = await apiCall("POST", `/SuperAdmin/organizations/${orgId}/fortinet/trace`, { enabled });
      if (updated) setStatus(updated);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to change diagnostic tracing.");
    } finally { setTraceSaving(false); }
  }, [apiCall, orgId]);

  // Automatic scanning (FortinetConfig.IsEnabled): off pauses scheduled scans only; Scan now and
  // Test connection still work. Same gate as the trace switch (CanManageFortinetTracing).
  const handleAutoScan = useCallback(async (enabled) => {
    setAutoScanSaving(true); setError(null);
    try {
      const updated = await apiCall("POST", `/SuperAdmin/organizations/${orgId}/fortinet/auto-scan`, { enabled });
      if (updated) setStatus(updated);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to change automatic scanning.");
    } finally { setAutoScanSaving(false); }
  }, [apiCall, orgId]);

  // Enrich risks: queues the org's Fortinet risks that have no AI analysis yet for the background
  // GRC enrichment worker. With a category, re-analyses every risk of that category, analysed or not.
  // Same gate as the trace switch (CanManageFortinetTracing).
  const handleEnrich = useCallback(async (reanalyze) => {
    setEnriching(true); setEnrichMessage(null);
    try {
      const query = reanalyze ? `?reanalyze=${encodeURIComponent(reanalyze)}` : "";
      const data = await apiCall("POST", `/SuperAdmin/organizations/${orgId}/fortinet/enrich${query}`);
      const n = data?.queued ?? 0;
      setEnrichMessage(reanalyze ? `Queued ${n} risks for re-analysis.` : `Queued ${n} risks for enrichment.`);
    } catch (err) {
      setEnrichMessage(err?.response?.data?.message || "Failed to queue risks for enrichment.");
    } finally { setEnriching(false); }
  }, [apiCall, orgId]);

  // TEMPORARY (revert this commit): shown only when the server says canRunDiagnostics
  // (CanRunFortinetDiagnostics: SuperAdmin listed in Fortinet:DiagnosticOperatorUserIds).
  const handleTest = useCallback(async () => {
    setTesting(true); setTestResult(null);
    try {
      const data = await apiCall("POST", `/SuperAdmin/organizations/${orgId}/fortinet/test-connection`);
      setTestResult({
        diagnostics: data?.diagnostics || [],
        managedDevices: data?.managedDevices ?? null,
        signalCount: data?.signalCount ?? 0,
      });
    } catch (err) {
      setTestResult({ message: err?.response?.data?.message || "Test failed — see server logs." });
    } finally { setTesting(false); }
  }, [apiCall, orgId]);

  const sources = status?.configured
    ? [status.fortigateUrlSet && "FortiGate", status.fortimanagerUrlSet && "FortiManager"].filter(Boolean)
    : [];

  return (
    <Modal isOpen={!!org} onClose={onClose} title={`Fortinet diagnostics - ${org?.organizationName || ""}`} size="md">
      <div data-testid="superadmin-fortinet-panel">
        {error && (
          <div className="mb-4 p-3 bg-risk-critical/10 border border-risk-critical/30 rounded-lg text-risk-critical text-sm">
            {error}
          </div>
        )}
        {loading && <LoadingSpinner />}
        {!loading && status && !status.configured && (
          <p className="text-sm text-content-muted">Fortinet is not configured for this organization.</p>
        )}
        {!loading && status?.configured && (
          <div className="space-y-3">
            <div className="text-sm text-content-secondary">
              <p>
                Fortinet is configured: {sources.length > 0 ? sources.join(" and ") : "no source URL saved"}.
              </p>
              {status.fortigateUrl && <p className="text-xs text-content-muted">FortiGate: {status.fortigateUrl}</p>}
              {status.fortimanagerUrl && <p className="text-xs text-content-muted">FortiManager: {status.fortimanagerUrl}</p>}
            </div>
            <FortinetLastScan className="" lastScannedAt={status.lastScannedAt}
              lastScanStatus={status.lastScanStatus} lastScanMessage={status.lastScanMessage} />
            {canManageFortinetTracing() ? (
              <div className="pt-3 border-t border-border">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!status.isEnabled} disabled={autoScanSaving}
                    onChange={e => handleAutoScan(e.target.checked)}
                    className="rounded accent-orange-500" />
                  <span className="text-xs font-medium text-gray-600">Automatic scanning</span>
                </label>
                <p className="text-xs text-gray-400 mt-1 ml-5">
                  {status.isEnabled
                    ? "Scheduled scans run for this organization."
                    : "Scheduled scans are paused. Scan now and Test connection still work."}
                </p>
              </div>
            ) : (
              <div className="text-xs text-gray-500">
                Automatic scanning is {status.isEnabled ? "on" : "paused"}
              </div>
            )}
            {canManageFortinetTracing() && (
              <div className="pt-3 border-t border-border">
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => handleEnrich()} disabled={enriching}
                    className="flex items-center gap-1.5 text-xs px-3 py-2 border border-orange-200 text-orange-700 rounded-lg font-semibold hover:bg-orange-50 disabled:opacity-50 active:scale-95 transition-all">
                    {enriching ? "Queuing…" : "Enrich risks"}
                  </button>
                  <button onClick={() => handleEnrich("FirewallPolicy")} disabled={enriching}
                    className="flex items-center gap-1.5 text-xs px-3 py-2 border border-orange-200 text-orange-700 rounded-lg font-semibold hover:bg-orange-50 disabled:opacity-50 active:scale-95 transition-all">
                    Re-analyse firewall policy risks
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Enrich risks queues this organization's Fortinet risks that have no AI analysis yet. Re-analyse
                  firewall policy risks redoes the AI analysis of every firewall policy risk, replacing its
                  remediation steps (and the remediation's, while it is not started and has no owner) and
                  recalculating its financial impact. Runs in the background; about 10–20 seconds per risk.
                </p>
                {enrichMessage && (
                  <p className="text-xs text-content-secondary mt-1" data-testid="fortinet-enrich-message">{enrichMessage}</p>
                )}
              </div>
            )}
            {status.canRunDiagnostics && (
              <div>
                <button onClick={handleTest} disabled={testing}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 border border-orange-200 text-orange-700 rounded-lg font-semibold hover:bg-orange-50 disabled:opacity-50 active:scale-95 transition-all">
                  {testing ? "Testing…" : "Test connection"}
                </button>
                {testResult && <FortinetTestResult className="mt-2 pt-2 border-t border-orange-200 space-y-1" result={testResult} />}
              </div>
            )}
            <FortinetTraceSwitch className="pt-3 border-t border-border" readOnlyClassName="text-xs text-gray-500"
              traceUntil={status.traceUntil} canManage={canManageFortinetTracing()}
              saving={traceSaving} onChange={handleTrace} />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SuperAdminFortinetPanel;
